import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Box,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useState } from 'react';
import {
  skillLevels,
  validatorsUserRoles
} from '../../../common/utils/constants';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import { errorToast, successToast } from '../../../common/utils/alert';
import CustomAutocomplete from '../../../common/widgets/CustomAutocomplete';
import {
  CREATE_MEMBER_SKILL,
  EDIT_MEMBER_SKILL,
  GET_SKILLS,
  GET_SKILL_VALIDATOR,
  GET_SYSTEM_ROLES,
  GET_VALIDATORS
} from '../../gql';
import { ISkillsData } from '../../type';
import { debounce } from 'lodash';
import { RootState } from '../../../common/utils/root-state';
import { useSelector } from 'react-redux';
import { UserRoleAppFormat } from '../../../common/utils/enum';

interface IToolFormProps {
  edit?: boolean;
  memberSkillId?: string;
  currentTool?: { id: string; name: string };
  currentLevel?: string;
  validatorId?: string;
  open?: boolean;
  handleClose: () => void;
  updateMemberSkillsCallback: () => Promise<void>;
  memberSkills: ISkillsData[] | undefined;
  memberId: string;
}

const AddToolsForm: React.FC<IToolFormProps> = ({
  edit,
  memberSkillId,
  currentTool,
  currentLevel,
  validatorId,
  handleClose,
  updateMemberSkillsCallback,
  memberSkills,
  memberId
}) => {
  const [tool, setTool] = useState<Record<string, string | number> | null>(
    null
  );
  const [level, setLevel] = useState<Record<string, string | number> | null>(
    null
  );
  const [validator, setValidator] = useState<Record<
    string,
    string | number
  > | null>(null);

  useEffect(() => {
    setTool(currentTool ?? null);
    const foundLevel = skillLevels.find((levl) => levl.id === currentLevel);
    setLevel(foundLevel ?? null);
  }, [memberSkillId]);

  const [errors, setErrors] = useState<{
    toolError: boolean;
    levelError: boolean;
    validatorError: boolean;
  }>({ toolError: false, levelError: false, validatorError: false });

  const currentMember = useSelector(
    (state: RootState) => state.profileData.profileData
  );

  const { data: toolsData } = useQuery(GET_SKILLS);
  const tools = toolsData?.getSkills || [];
  const { data: systemRoles } = useQuery(GET_SYSTEM_ROLES, {
    variables: { roles: validatorsUserRoles }
  });
  const roles: Array<{ userRole: string; id: string }> =
    systemRoles?.getSystemRoles ?? [];
  const [getValidators, { data: validatorsData, loading: validatorsLoading }] =
    useLazyQuery(GET_VALIDATORS);
  const { data: validatorData } = useQuery(GET_SKILL_VALIDATOR, {
    variables: { id: validatorId }
  });

  useEffect(() => {
    setValidator(validatorData?.getMember ?? null);
  }, [validatorData?.getMember]);

  const validators: Array<Record<string, string | number>> =
    validatorsData?.getMembers.data || [];

  const [createMemberSkill, { loading: addLoading }] =
    useMutation(CREATE_MEMBER_SKILL);
  const [editMemberSkill, { loading: editLoading }] =
    useMutation(EDIT_MEMBER_SKILL);

  const close = (): void => {
    setTool(null);
    setLevel(null);
    setValidator(null);
    setErrors({
      toolError: false,
      levelError: false,
      validatorError: false
    });
    handleClose();
  };

  useEffect(() => {
    if (!edit) {
      setTool(null);
      setLevel(null);
      setValidator(null);
    }
  }, [edit]);

  const handleAdd = (): void => {
    if (!tool || !level || !validator) {
      setErrors({
        toolError: !tool,
        levelError: !level,
        validatorError: !validator
      });
      return;
    }

    const mutationVariables = {
      memberId,
      skillId: tool.id,
      proficiencyLevel: level.id,
      validatorId: validator.id
    };

    const mutationPromise = edit
      ? editMemberSkill({
          variables: {
            actionUserId: currentMember?.memberId,
            ...mutationVariables,
            memberSkillId,
            generateApprovalRequest: !currentMember?.roles.includes(
              UserRoleAppFormat.SuperAdmin
            ),
            generateNotification: currentMember?.roles.includes(
              UserRoleAppFormat.SuperAdmin
            )
          }
        })
      : createMemberSkill({ variables: mutationVariables });

    close();

    mutationPromise
      .then(async () => {
        if (currentMember?.roles.includes(UserRoleAppFormat.SuperAdmin)) {
          successToast('Tool updated successfully');
        } else {
          successToast('Request sent to the validator for approval.');
        }
        await updateMemberSkillsCallback();
      })
      .catch((error) => {
        console.error(error);
        errorToast('Adding tools failed. Please try again.');
      });
  };

  let filteredTools: ISkillsData[] | undefined;
  if (memberSkills) {
    const memberSkillIds = memberSkills.map((skill) => skill.skill.id);

    filteredTools = tools.filter(
      (toolData: ISkillsData) => !memberSkillIds.includes(toolData.id)
    );
  }

  const debouncedGetValidators = debounce((searchTerm: string) => {
    void getValidators({
      variables: {
        displayName: searchTerm,
        roles: roles.map((item) => item.id)
      }
    });
  }, 300);

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleValidatorInputChange = (
    event: React.ChangeEvent<{}>,
    value: string
  ) => {
    debouncedGetValidators(value);
  };

  return (
    <Dialog
      open={true}
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: '28px',
          backgroundColor: '#f4f2fd',
          padding: '24px',
          width: '60%'
        }
      }}
    >
      <DialogContent sx={{ padding: '0' }}>
        <Box sx={{ justifyContent: 'space-between', display: 'flex' }}>
          <DialogContentText
            sx={{
              display: 'flex',
              fontSize: '24px',
              textAlign: 'center',
              color: '#1A1B1F',
              marginTop: '12px',
              flexWrap: 'wrap',
              wordWrap: 'break-word'
            }}
          >
            {edit ? 'Edit Tool or Technology' : 'Add New Tool or Technology'}
          </DialogContentText>
          <IconButton
            onClick={edit ? handleClose : close}
            sx={{ height: '24px', width: '24px' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box
          sx={{
            marginTop: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px'
          }}
        >
          <CustomAutocomplete
            label="Tool/Technology"
            value={tool}
            error={errors.toolError}
            options={filteredTools !== undefined ? filteredTools : tools}
            onChange={(newValue) => {
              setTool(newValue);
              setErrors({ ...errors, toolError: false });
            }}
            isEdit={edit}
          />
          <CustomAutocomplete
            label="Level of Proficiency"
            value={level}
            error={errors.levelError}
            options={skillLevels}
            onChange={(newValue) => {
              setLevel(newValue);
              setErrors({ ...errors, levelError: false });
            }}
          />
          <CustomAutocomplete
            label="Validator"
            value={validator}
            error={errors.validatorError}
            options={validators.filter(
              (user) => user.id !== currentMember?.memberId
            )}
            onChange={(newValue) => {
              setValidator(newValue);
              setErrors({ ...errors, validatorError: false });
            }}
            onInputChange={handleValidatorInputChange}
            loading={validatorsLoading}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          sx={{
            textTransform: 'none',
            fontSize: '18px',
            color: '#295DAB',
            fontWeight: '500'
          }}
          onClick={edit ? handleClose : close}
        >
          {edit ? 'Discard' : 'Cancel'}
        </Button>
        <Button
          sx={{
            textTransform: 'none',
            fontSize: '18px',
            color: '#295DAB',
            fontWeight: '500'
          }}
          onClick={() => handleAdd()}
          autoFocus
          disabled={edit ? editLoading : addLoading}
        >
          {edit ? 'Save' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddToolsForm;
